/**
 * Pure helpers for resolving shadow-map refresh-rate values.
 *
 * Kept separate from VisualProfile.ts so it can be unit-tested without
 * importing Babylon.js (the numeric constants are reproduced here; they
 * map 1:1 to RenderTargetTexture.REFRESHRATE_RENDER_* statics).
 *
 * Babylon 9.x constant values (verified from @babylonjs/core typings):
 *   RENDER_ONCE             = 0
 *   RENDER_ONEVERYFRAME     = 1
 *   RENDER_ONEVERYTWOFRAMES = 2
 *
 * Why every-2-frames by default:
 *   The cats are kinematic shadow casters — a fully-frozen map (RENDER_ONCE)
 *   would stick their shadows in place even as they move (visible bug).
 *   Every-2-frames halves the depth-pass + 32-tap blur cost while keeping
 *   shadows tracking the characters with at most one-frame lag.
 *
 * DEFERRED (pending human visual-quality call):
 *   - 1024 → 512 shadow map size (would further halve memory, costs quality)
 *   - Per-level RENDER_ONCE opt-in for genuinely static scenes (no player
 *     shadow casters registered). Wire via `shadowRefreshRate: SHADOW_ONCE`
 *     in the LightRigPreset when ready.
 */

/** Numeric value for RenderTargetTexture.REFRESHRATE_RENDER_ONCE (= 0). */
export const SHADOW_ONCE = 0 as const;

/** Numeric value for RenderTargetTexture.REFRESHRATE_RENDER_ONEVERYFRAME (= 1). */
export const SHADOW_EVERY_FRAME = 1 as const;

/**
 * Numeric value for RenderTargetTexture.REFRESHRATE_RENDER_ONEVERYTWOFRAMES (= 2).
 * This is the project default — halves depth-pass cost, one-frame shadow lag.
 */
export const SHADOW_EVERY_TWO_FRAMES = 2 as const;

/**
 * Resolve the shadow-map refresh rate from an optional per-level override.
 *
 * @param profileOption - Value from `KeyLightPreset.shadowRefreshRate`.
 *   Pass `undefined` to use the project default.
 * @param defaultRate - Fallback when `profileOption` is not set.
 *   Defaults to `SHADOW_EVERY_TWO_FRAMES`.
 * @returns The numeric refresh-rate constant to assign to
 *   `shadowGenerator.getShadowMap()!.refreshRate`.
 */
export function resolveShadowRefreshRate(
  profileOption: number | undefined,
  defaultRate: number = SHADOW_EVERY_TWO_FRAMES,
): number {
  return profileOption ?? defaultRate;
}
